<template>
  <AdminLayout page-title="Analytics Dashboard">
    <div class="mx-auto">
      <div
        v-if="loading"
        class="flex items-center justify-center py-24 text-on-surface-variant"
      >
        Loading analytics...
      </div>

      <template v-else>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="dashboard-card bg-surface rounded-xl p-6 border border-outline-variant/30">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-on-surface-variant">Total Views</p>
              <p class="text-2xl font-bold text-on-surface mt-1">{{ analytics.totalViews }}</p>
            </div>
            <div class="p-3 bg-primary/10 rounded-lg">
              <svg class="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
              </svg>
            </div>
          </div>
        </div>

        <div class="dashboard-card bg-surface rounded-xl p-6 border border-outline-variant/30">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-on-surface-variant">Articles With Views</p>
              <p class="text-2xl font-bold text-on-surface mt-1">{{ analytics.uniqueArticles }}</p>
              <p class="text-xs text-on-surface-variant mt-1">
                of {{ analytics.publishedArticles }} published
              </p>
            </div>
            <div class="p-3 bg-secondary/10 rounded-lg">
              <svg class="w-6 h-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
              </svg>
            </div>
          </div>
        </div>

        <div class="dashboard-card bg-surface rounded-xl p-6 border border-outline-variant/30">
          <div class="flex items-center justify-between">
            <div>
              <p class="text-sm font-medium text-on-surface-variant">Avg Views/Article</p>
              <p class="text-2xl font-bold text-on-surface mt-1">{{ analytics.averageViews }}</p>
            </div>
            <div class="p-3 bg-primary-container/20 rounded-lg">
              <svg class="w-6 h-6 text-primary-container" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"></path>
              </svg>
            </div>
          </div>
        </div>

        <div class="dashboard-card bg-surface rounded-xl p-6 border border-outline-variant/30">
          <div class="flex items-center justify-between">
            <div class="min-w-0 flex-1">
              <p class="text-sm font-medium text-on-surface-variant">Top Article</p>
              <p class="text-lg font-bold text-on-surface mt-1 truncate">{{ analytics.topArticle?.title || 'N/A' }}</p>
              <p class="text-sm text-on-surface-variant">{{ analytics.topArticle?.views || 0 }} views</p>
            </div>
            <div class="p-3 bg-brand-accent/20 rounded-lg flex-shrink-0 ml-2">
              <svg class="w-6 h-6 text-secondary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z"></path>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <div class="dashboard-card bg-surface rounded-xl border border-outline-variant/30 overflow-hidden">
        <div class="px-6 py-4 border-b border-outline-variant/30">
          <h2 class="text-lg font-medium text-on-surface">Article Views Details</h2>
        </div>

        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-surface-container-low">
              <tr>
                <th class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider">
                  Article Title
                </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider">
                  Category
                </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider">
                  Views
                </th>
                <th class="px-6 py-4 text-left text-xs font-semibold text-on-surface uppercase tracking-wider">
                  Last Viewed
                </th>
              </tr>
            </thead>
            <tbody class="divide-y divide-outline-variant/30">
              <tr v-for="article in analytics.articleViews" :key="article.id" class="hover:bg-surface-container-low transition-colors">
                <td class="px-6 py-4">
                  <div class="text-sm font-medium text-on-surface">{{ article.title }}</div>
                </td>
                <td class="px-6 py-4 whitespace-nowrap">
                  <span class="px-3 py-1 text-xs font-medium bg-primary/10 text-primary rounded-full">
                    {{ article.category }}
                  </span>
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                  {{ article.views }}
                </td>
                <td class="px-6 py-4 whitespace-nowrap text-sm text-on-surface-variant">
                  {{ formatDate(article.lastViewed) }}
                </td>
              </tr>
              <tr v-if="analytics.articleViews.length === 0">
                <td colspan="4" class="px-6 py-12 text-center text-on-surface-variant">
                  No article views data available yet.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
      </template>
    </div>
  </AdminLayout>
</template>

<script setup>
import { ref, onMounted } from "vue";
import { useRouter } from "vue-router";
import { authService } from "../../utils/auth.js";
import { getAnalytics } from "../../utils/analytics.js";
import AdminLayout from "../../components/admin/AdminLayout.vue";
import { showAdminToast } from "../../utils/adminToast.js";

const router = useRouter();

const loading = ref(true);
const analytics = ref({
  totalViews: 0,
  uniqueArticles: 0,
  publishedArticles: 0,
  averageViews: 0,
  topArticle: null,
  articleViews: [],
});

const formatDate = (dateString) => {
  if (!dateString) return "Never";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const loadAnalytics = async () => {
  loading.value = true;
  try {
    analytics.value = await getAnalytics();
  } catch (error) {
    console.error("Error loading analytics:", error);
    showAdminToast("Error loading analytics data", "error");
  } finally {
    loading.value = false;
  }
};

onMounted(async () => {
  if (!authService.isAuthenticated()) {
    router.push("/admin/login");
    return;
  }

  await loadAnalytics();
});
</script>

<style scoped>
.dashboard-card {
  box-shadow: 0 10px 30px rgba(21, 66, 18, 0.08);
}
</style>
